import * as React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useUpdatePoint } from 'src/features/points/api/useUpdatePoint';
import { type Point } from 'src/features/points/types';
import pointsMessages from 'src/features/points/utils/messages';
import { toast } from 'src/registry/hooks/UseToast';
import { Switch } from 'src/registry/ui/Switch';
import { generalErrorMessages } from 'src/utils/messages';

interface CellNormalCardEnterProps {
    point: Point;
    onUpdated?: () => void;
}

function CellNormalCardEnter({ point, onUpdated }: CellNormalCardEnterProps) {
    const { parkingId } = useParams();
    const { formatMessage } = useIntl();
    // A null value is treated as enabled (true); only an explicit false is disabled
    const [checked, setChecked] = React.useState(point.normal_card_enter !== false);

    React.useEffect(() => {
        setChecked(point.normal_card_enter !== false);
    }, [point.normal_card_enter]);

    const { mutate, isPending } = useUpdatePoint({
        onSuccess: () => {
            toast({
                title: formatMessage(pointsMessages.updateSuccess),
                description: formatMessage(pointsMessages.updateSuccessDescription),
            });
            onUpdated?.();
        },
        onError: (_error, variables) => {
            // Revert the optimistic toggle back to its previous value
            setChecked(!variables.normal_card_enter);
            toast({
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description: formatMessage(generalErrorMessages.unknownError),
                variant: 'destructive',
            });
        },
    });

    const handleChange = (value: boolean) => {
        if (!parkingId) return;
        setChecked(value);
        mutate({ parkingId, pointId: point.id, normal_card_enter: value });
    };

    return (
        <span onClick={(event) => event.stopPropagation()}>
            <Switch checked={checked} onCheckedChange={handleChange} disabled={isPending} />
        </span>
    );
}

export default CellNormalCardEnter;
